import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Castle, Crown, Shield, Star, Trophy } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { useGetCocLinkQuery, useGetPlayerQuery } from '@/services/qclansApi';
import { classifyQclansError, qclansErrorKey } from '@/services/qclansErrors';
import type { PlayerHero, PlayerHeroEquipment } from '@/types/qclans';
import { cn } from '@/lib/cn';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatNumero } from '@/lib/fmt';

/**
 * My CoC profile (`GET /v1/players/:tag`) for the linked account(s). When more
 * than one account is linked, a chip row switches the active tag. Renders Town
 * Hall, key stats, heroes + equipment, and a link out to the player's clan.
 */
export function CocProfile() {
  const { t } = useTranslation('coc');
  const lingua = useLinguaFormato();
  const haptics = useHaptics();

  const linkQuery = useGetCocLinkQuery();
  const accounts = linkQuery.data?.accounts ?? [];

  const [activeTag, setActiveTag] = useState<string | null>(null);
  // Default to the first linked account once the link list resolves.
  useEffect(() => {
    if (!activeTag && accounts.length > 0) setActiveTag(accounts[0].playerTag);
  }, [accounts, activeTag]);

  const playerQuery = useGetPlayerQuery(activeTag ?? '', { skip: !activeTag });
  const player = playerQuery.data;

  if (linkQuery.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <Shield aria-hidden className="h-10 w-10 text-fg-muted" />
        <p className="text-sm text-fg-muted">{t('profile.noLinked')}</p>
        <Link
          to="/coc/link"
          className="rounded-xl bg-brand px-4 py-2.5 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
        >
          {t('profile.linkCta')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <h1 className="text-2xl font-bold text-fg">{t('profile.title')}</h1>

      {/* Account switcher (only when multiple linked) */}
      {accounts.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {accounts.map((acc) => (
            <button
              key={acc.playerTag}
              type="button"
              onClick={() => {
                haptics.selection();
                setActiveTag(acc.playerTag);
              }}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                acc.playerTag === activeTag
                  ? 'bg-brand text-brand-fg'
                  : 'bg-bg-tertiary text-fg-muted',
              )}
            >
              {acc.playerName}
            </button>
          ))}
        </div>
      )}

      {playerQuery.isLoading ? (
        <Loader />
      ) : playerQuery.error ? (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qclansErrorKey(classifyQclansError(playerQuery.error)))}
        </p>
      ) : player ? (
        <>
          {/* Header */}
          <header className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Crown aria-hidden className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <h2 className="truncate text-lg font-bold text-fg">{player.name}</h2>
              <span className="text-[11px] tabular-nums text-fg-muted">{player.tag}</span>
              <span className="mt-0.5 text-[11px] font-semibold text-brand">
                {t('profile.townHall', { level: player.townHallLevel })}
              </span>
            </div>
            {player.league?.iconUrls?.small && (
              <img
                src={player.league.iconUrls.small}
                alt={player.league.name}
                className="h-12 w-12 shrink-0"
              />
            )}
          </header>

          {/* Stats grid */}
          <section className="grid grid-cols-3 gap-2">
            <Stat label={t('profile.stats.trophies')} value={formatNumero(player.trophies, lingua)} icon={<Trophy className="h-4 w-4" />} />
            <Stat label={t('profile.stats.best')} value={formatNumero(player.bestTrophies, lingua)} icon={<Star className="h-4 w-4" />} />
            <Stat label={t('profile.stats.warStars')} value={formatNumero(player.warStars, lingua)} icon={<Star className="h-4 w-4" />} />
            <Stat label={t('profile.stats.level')} value={String(player.expLevel)} />
            {player.attackWins != null && (
              <Stat label={t('profile.stats.attackWins')} value={formatNumero(player.attackWins, lingua)} />
            )}
            {player.donations != null && (
              <Stat label={t('profile.stats.donations')} value={formatNumero(player.donations, lingua)} />
            )}
          </section>

          {/* Clan */}
          <section className="rounded-xl bg-bg-secondary p-4">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-fg-muted">
              {t('profile.clanLabel')}
            </h3>
            {player.clan ? (
              <Link
                to={`/coc/clan?tag=${encodeURIComponent(player.clan.tag)}`}
                onClick={() => haptics.impact('light')}
                className="flex items-center gap-3 transition-opacity active:opacity-80"
              >
                {player.clan.badgeUrls?.small ? (
                  <img src={player.clan.badgeUrls.small} alt="" className="h-8 w-8" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded bg-brand/15 text-brand">
                    <Castle className="h-4 w-4" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-fg">{player.clan.name}</p>
                  <p className="text-[11px] tabular-nums text-fg-muted">{player.clan.tag}</p>
                </div>
                <span className="text-xs font-semibold text-brand">{t('profile.viewClan')}</span>
              </Link>
            ) : (
              <p className="text-sm text-fg-muted">{t('profile.noClan')}</p>
            )}
          </section>

          {/* Heroes */}
          <HeroSection title={t('profile.heroes')} heroes={player.heroes ?? []} emptyLabel={t('profile.noHeroes')} />

          {/* Hero equipment */}
          {(player.heroEquipment?.length ?? 0) > 0 && (
            <EquipmentSection title={t('profile.heroEquipment')} items={player.heroEquipment ?? []} />
          )}
        </>
      ) : null}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-bg-secondary p-3 text-center">
      {icon && <span className="mb-1 flex justify-center text-brand">{icon}</span>}
      <p className="text-base font-bold tabular-nums text-fg">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-fg-muted">{label}</p>
    </div>
  );
}

function HeroSection({
  title,
  heroes,
  emptyLabel,
}: {
  title: string;
  heroes: PlayerHero[];
  emptyLabel: string;
}) {
  // Show only home-village heroes to keep the list focused; CoC tags them with
  // village === 'home'. If none match (older payloads), fall back to all.
  const home = heroes.filter((h) => h.village === 'home');
  const list = home.length > 0 ? home : heroes;
  return (
    <section className="rounded-xl bg-bg-secondary p-4">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-fg-muted">{title}</h3>
      {list.length === 0 ? (
        <p className="text-sm text-fg-muted">{emptyLabel}</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {list.map((h) => (
            <li key={h.name} className="flex items-center justify-between text-sm">
              <span className="text-fg">{h.name}</span>
              <span className="tabular-nums text-fg-muted">
                {h.level}
                <span className="text-[10px]">/{h.maxLevel}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EquipmentSection({ title, items }: { title: string; items: PlayerHeroEquipment[] }) {
  const home = items.filter((i) => i.village === 'home');
  const list = home.length > 0 ? home : items;
  return (
    <section className="rounded-xl bg-bg-secondary p-4">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-fg-muted">{title}</h3>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {list.map((i) => (
          <li key={i.name} className="flex items-center justify-between text-sm">
            <span className="truncate text-fg">{i.name}</span>
            <span className="shrink-0 tabular-nums text-fg-muted">
              {i.level}
              <span className="text-[10px]">/{i.maxLevel}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
